import { cn } from "../lib/cn";

export interface RateBar {
  label: string;
  /** 0..1, or null when the denominator was empty. */
  value: number | null | undefined;
  /** The counts behind it, e.g. "6 of 8". Shown so the rate can be checked. */
  detail?: string;
}

export interface RateBarsProps {
  bars: readonly RateBar[];
  /**
   * `stacked` puts the label and figure over the bar, for a screen given over to rates.
   * `inline` reads label, bar, figure along one line, for a panel that only glances.
   */
  layout?: "stacked" | "inline";
  className?: string;
}

/**
 * A row of shares on one 0–100% axis.
 *
 * One hue throughout, not a colour per row: the rows are already named, so colour would
 * be carrying no information a reader does not already have — and three saturated hues
 * on three bars of the same measure invites a comparison of the colours rather than of
 * the lengths.
 *
 * A null rate draws no bar and says so in words. "Nothing was candled" and "nothing was
 * fertile" are different findings, and a zero-length bar renders them identically.
 */
export function RateBars({ bars, layout = "stacked", className }: RateBarsProps) {
  if (layout === "inline") {
    return (
      <ul className={cn("flex flex-col gap-3", className)}>
        {bars.map((bar) => {
          const known = bar.value !== null && bar.value !== undefined;
          const percent = known ? Math.round(bar.value! * 100) : null;

          return (
            <li key={bar.label} className="flex items-center gap-3 text-subtitle text-on-surface">
              <span className="w-17.5 shrink-0">{bar.label}</span>
              <div
                role="progressbar"
                aria-label={bar.label}
                aria-valuenow={percent ?? undefined}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuetext={known ? `${percent} percent` : "No data"}
                className="flex h-2.5 flex-1 overflow-hidden rounded-full bg-surface-container-lowest"
              >
                {known ? (
                  <div
                    className="rounded-full bg-primary transition-[width] duration-300"
                    style={{ width: `${Math.min(100, Math.max(0, percent!))}%` }}
                  />
                ) : null}
              </div>
              <span className="w-9 shrink-0 text-right font-bold" data-numeric>
                {known ? `${percent}%` : "—"}
              </span>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul className={cn("flex flex-col gap-md", className)}>
      {bars.map((bar) => {
        const known = bar.value !== null && bar.value !== undefined;
        const percent = known ? Math.round(bar.value! * 100) : null;

        return (
          <li key={bar.label} className="flex flex-col gap-xs">
            <div className="flex items-baseline justify-between gap-md">
              <span className="text-body text-on-surface">{bar.label}</span>

              <span className="text-data text-on-surface" data-numeric>
                {known ? `${percent}%` : "No data"}
                {bar.detail ? (
                  <span className="pl-sm text-label-caps uppercase text-on-surface-variant">
                    {bar.detail}
                  </span>
                ) : null}
              </span>
            </div>

            <div
              role="progressbar"
              aria-label={bar.label}
              aria-valuenow={percent ?? undefined}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuetext={known ? `${percent} percent` : "No data"}
              className="h-2 w-full overflow-hidden rounded-full bg-surface-container-high"
            >
              {known ? (
                <div
                  className="h-full rounded-full transition-[width] duration-300"
                  style={{
                    // Clamped for display only. A rate above 100% means the data is
                    // wrong, and the number beside the bar still says so.
                    width: `${Math.min(100, Math.max(0, percent!))}%`,
                    background: "var(--color-chart-series-1)",
                  }}
                />
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export interface TrendPoint {
  label: string;
  first: number;
  second: number;
}

export interface TrendChartProps {
  points: readonly TrendPoint[];
  firstLabel: string;
  secondLabel: string;
  className?: string;
}

/**
 * Two counts per period, as paired bars.
 *
 * Bars rather than lines because a season is a bucket, not a continuum — nothing happens
 * between them, and a line drawn across the gap implies a rate of change that does not
 * exist. Two series means a legend is present, and each pair is separated by a surface
 * gap so adjacent fills do not read as one block.
 */
export function TrendChart({ points, firstLabel, secondLabel, className }: TrendChartProps) {
  if (points.length === 0) {
    return (
      <p className={cn("text-body text-on-surface-variant", className)}>
        Not enough seasons recorded yet.
      </p>
    );
  }

  const max = Math.max(1, ...points.flatMap((point) => [point.first, point.second]));

  return (
    <figure className={cn("flex flex-col gap-sm", className)}>
      {/* Two series, so a legend is always present — identity is never colour alone. */}
      <figcaption className="flex flex-wrap items-center gap-md">
        {[
          { label: firstLabel, token: "var(--color-chart-series-1)" },
          { label: secondLabel, token: "var(--color-chart-series-2)" },
        ].map((series) => (
          <span key={series.label} className="flex items-center gap-xs">
            <span
              aria-hidden
              className="size-2 rounded-full"
              style={{ background: series.token }}
            />
            <span className="text-label-caps uppercase text-on-surface-variant">
              {series.label}
            </span>
          </span>
        ))}
      </figcaption>

      <div className="flex items-end gap-md overflow-x-auto pb-xs">
        {points.map((point) => (
          <div key={point.label} className="flex min-w-16 flex-1 flex-col items-center gap-xs">
            <div className="flex h-32 w-full items-end justify-center gap-xs">
              {[
                { value: point.first, token: "var(--color-chart-series-1)", label: firstLabel },
                { value: point.second, token: "var(--color-chart-series-2)", label: secondLabel },
              ].map((series) => (
                <div
                  key={series.label}
                  title={`${point.label} · ${series.label}: ${series.value}`}
                  className="w-4 rounded-t-sm"
                  style={{
                    // Floor of 2px so a zero still shows as a mark rather than vanishing,
                    // which would read as missing data rather than as none.
                    height: `${Math.max(2, (series.value / max) * 128)}px`,
                    background: series.token,
                  }}
                />
              ))}
            </div>

            <span className="text-label-caps uppercase text-on-surface-variant" data-numeric>
              {point.label}
            </span>
          </div>
        ))}
      </div>
    </figure>
  );
}
